import { useStore } from '../store';
import { speciesArtUrl } from '../speciesArt';
import { speciesReference } from '../species-reference';
import { loadCatalogs } from '../../config/catalogs';
import type { UiAnimal } from '../bridge';
import { cap, coatLabel, fmtMoney, speciesLabel } from '../bridge';
import { PairingSheet } from '../components/PairingSheet';
import { AnimalLifeLog } from '../components/AnimalLifeLog';
import { PairStatusLine, usePairRequest } from '../components/PairStatus';
import { ObserveSheet } from '../components/ObserveSheet';
import { Sheet } from '../components/Sheet';
import { TipCard } from '../components/TipCard';
import { IconChevron, IconDna, IconHerd, IconLeaf, IconPaw, IconRings, IconReserve } from '../components/icons';
import { useEffect, useRef, useState } from 'preact/hooks';
import { DEFAULT_HERD_FILTERS, applyHerdFilters, groupHerdAnimals, ageLabel, loadSavedHerdFilters, persistSavedHerdFilters, upsertSavedHerdFilter, deleteSavedHerdFilter, type HerdFilterState, type SavedHerdFilter } from '../herd-filters';
import { resolveMoveRows, moveOutcomeAgedOut } from '../move-report';

const stockMoney = (cents: number): string => (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function treatmentReason(reason:string):string {
  if(reason.startsWith('insufficient cash for treatment'))return 'Not enough cash for the vet fee';
  if(reason.startsWith('unknown animal'))return 'Animal is no longer on the ranch';
  if(reason.endsWith(' is not ill'))return 'Animal is no longer ill';
  return reason;
}

function AnimalRow({ a, onOpen, selecting, selected, onToggle }: { a: UiAnimal; onOpen(): void; selecting?: boolean; selected?: boolean; onToggle?(): void }) {
  const { sim } = useStore();
  const age = ageLabel(a.bornTick, sim.tick);
  const labelParts = [a.hybrid ? `${a.hybrid} (hybrid, ${a.species} dam)` : cap(a.species), a.sex === 'M' ? 'Male' : 'Female', a.biome, a.enclosure ?? '', age, 'quality: -'].filter((p) => p.trim() !== '');
  return (
    <button class="card row" style={`border:var(--ui-border);width:100%;text-align:left;cursor:pointer;background:${selected === true ? 'var(--ui-parchment)' : 'var(--ui-parchment-soft)'}`} onClick={selecting === true ? onToggle : onOpen}>
      {selecting === true
        ? <span class="row-ic" aria-hidden>{selected === true ? '\u2611' : '\u2610'}</span>
        : <span class="row-ic"><IconPaw /></span>}
      <span class="row-main">
        <div class="row-value">{a.name}</div>
        <div class="row-label">{labelParts.join(' - ')}</div>
      </span>
      {(a.rarity === 'rare' || a.rarity === 'exotic') && <span class="pill pill-amber">{a.rarity}</span>}
      {a.ill && <span class="pill pill-red">sick</span>}
      {a.quarantined === true && <span class="pill">quarantined</span>}
      {sim.pregnancies.some((p) => p.damId === a.id) && <span class="pill pill-amber">pregnant</span>}
      <span class="row-chev"><IconChevron /></span>
    </button>
  );
}

const SALE_CHANNELS: Array<{ id: 'stock' | 'meat' | 'breeder' | 'collector'; name: string; hint: string }> = [
  { id: 'stock', name: 'Stock', hint: 'Default price. Each sale adds market saturation for the species.' },
  { id: 'meat', name: 'Meat', hint: 'Processor price. Ends the animal.' },
  { id: 'breeder', name: 'Breeder', hint: 'Pays 25% more for healthy, calm, breeding-age stock.' },
  { id: 'collector', name: 'Collector', hint: 'Pays 50% more, but only for rare species or rare coats.' },
];

/** Detail card per refs/mockup-herd-screen.png */
/** Destination picks can append requirement lines (quarantine, export licence) that push the
    Sell/Auction buttons and the lower auctions copy past the card's bottom edge. Scroll the
    hero card to its end (same smooth pattern as the Sell-or-auction toggle) so nothing clips. */
function scrollSellActionsIntoView(el: HTMLElement): void {
  window.setTimeout(() => {
    const sc = el.closest('.hero-card-scroll');
    if (sc instanceof HTMLElement) { sc.scrollTo({ top: sc.scrollHeight, behavior: 'smooth' }); }
  }, 30);
}

function AnimalDetail({ a, onChooseMate }: { a: UiAnimal; onChooseMate(): void }) {
  const { bridge, sim, openPanel } = useStore();
  const holdTimer = useRef<number | null>(null);
  const feedOnce = () => {
    if (!a.enclosure) { return; } // rejection surfaces as an alert; no fake ack
    bridge.send('animal.feed', { enclosureId: a.enclosure }); // contract: feeds the whole enclosure
  };
  // Press-and-hold repeats the feed with no popup; release stops it (done-when e).
  const feedDown = (e: { currentTarget: HTMLButtonElement; pointerId: number }) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    feedOnce();
    holdTimer.current = window.setInterval(feedOnce, 300);
  };
  const feedUp = () => {
    if (holdTimer.current !== null) { clearInterval(holdTimer.current); holdTimer.current = null; }
  };
  useEffect(() => feedUp, []);
  const act = (type: string, payload: Record<string, unknown>) => {
    bridge.send(type, payload); // success is silent; failures arrive as alerts
  };
  const [sellOpen, setSellOpen] = useState(false);
  const [channel, setChannel] = useState<'stock' | 'meat' | 'breeder' | 'collector'>('stock');
  const [dest, setDest] = useState('');
  const[auctionReserve,setAuctionReserve]=useState('0');const[auctionReview,setAuctionReview]=useState(false);
  const reserveCents=Math.round(Number(auctionReserve)*100);const validReserve=auctionReserve.trim()!==''&&Number.isFinite(Number(auctionReserve))&&Number(auctionReserve)>=0&&Number.isSafeInteger(reserveCents)&&Math.abs(Number(auctionReserve)*100-reserveCents)<0.0001;
  const [moveTarget, setMoveTarget] = useState('');
  const [moveAttempt, setMoveAttempt] = useState<{ requestId: string; preExcluded: ReadonlyMap<string, string> } | null>(null);
  const juris = sim.standing?.jurisdictions ?? [];
  const destDef = juris.find((j) => j.id === dest);
  const [reference, setReference] = useState<ReturnType<typeof speciesReference>>(null);
  useEffect(() => {
    let live = true;
    void loadCatalogs().then(() => { if (live) setReference(speciesReference(a.species)); });
    return () => { live = false; };
  }, [a.species]);
  const myPreg = sim.pregnancies.find((p) => p.damId === a.id);
  const sirePreg = sim.pregnancies.filter((p) => p.sireId === a.id);
  const pairReq = usePairRequest();
  useEffect(() => { pairReq.clear(); }, [a.id]);
  useEffect(() => { setMoveTarget(''); setMoveAttempt(null); }, [a.id]);
  const breed = () => { pairReq.sendAuto(a.id); };
  // Single-animal move: same herd.move command and per-attempt outcome matching as the
  // bulk flow below (send-time pre-exclusions, own outcome record by requestId).
  const sendMove = () => {
    if (!moveTarget) return;
    const preExcluded = new Map<string, string>();
    if (a.quarantined === true) preExcluded.set(a.id, 'quarantined');
    if (a.enclosure === moveTarget) preExcluded.set(a.id, 'already there');
    void bridge.sendTracked('herd.move', { animalIds: [a.id], enclosureId: moveTarget })
      .then((id) => { setMoveAttempt({ requestId: id ?? 'sent', preExcluded }); })
      .catch(() => { setMoveAttempt(null); }); // rejection surfaces as a command_rejected alert
  };
  const daysTo = (tick: number) => Math.max(0, Math.ceil((tick - sim.tick) / 24));
  const detailRow = (Icon: (p: Record<string, unknown>) => preact.JSX.Element, label: string, value: string) => (
    <div class="row">
      <span class="row-ic"><Icon /></span>
      <span class="row-main">
        <div class="row-label">{label}</div>
        <div class="row-value">{value}</div>
      </span>
      <span class="row-chev"><IconChevron /></span>
    </div>
  );
  const [artOk, setArtOk] = useState(true);
  useEffect(() => { setArtOk(true); }, [a.id]);
  return (
    <>
      <div class="hero-full">
        {artOk ? (
          <>
            <img class="hero-bg" src={speciesArtUrl(a.species)} alt="" aria-hidden="true" />
            <img src={speciesArtUrl(a.species)} alt={`${a.species} in the reserve`} onError={() => { setArtOk(false); }} />
          </>
        ) : (
          <div class="hero-art-fallback"><IconHerd /></div>
        )}
      </div>
    <div class="card-ornate hero-card">
      <IconReserve class="card-watermark" />
      <div class="hero-card-scroll">
      <div style="text-align:center;padding:8px 0 2px">
        <div class="display" style="font-size:26px">{a.name.toLowerCase().startsWith(a.species.toLowerCase()) ? a.name : `${a.name} - ${cap(a.species)}`}</div>
        {(a.rare || myPreg || a.ill || a.quarantined === true) && (
          <div style="margin-top:8px;display:flex;gap:6px;justify-content:center">
            {a.ill && <span class="pill pill-red">Sick</span>}
            {a.quarantined === true && <span class="pill">Quarantined</span>}
            {a.rare && <span class="pill pill-amber">Rare</span>}
            {myPreg && <span class="pill pill-amber">Pregnant - due in {daysTo(myPreg.dueTick)}d</span>}
          </div>
        )}
        {sirePreg.length > 0 && <div class="row-label" style="margin-top:6px">Sire of {sirePreg.length} pending {sirePreg.length === 1 ? 'birth' : 'births'}</div>}
      </div>
      <TipCard inSheet />
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
        <button
          class="btn btn-primary"
          onPointerDown={feedDown} onPointerUp={feedUp} onPointerCancel={feedUp} onPointerLeave={feedUp}
        >
          <IconLeaf />Feed
        </button>
        <button class="btn" disabled={pairReq.pending} onClick={() => { breed(); }}>
          <IconRings />Breed
        </button>
      </div>
      <div class="row-label" style="margin-top:4px;text-transform:none;letter-spacing:.01em;white-space:normal">Hold Feed to keep feeding the whole pen; release to stop. Feeding uses produce and shows in the ledger.</div>
      <button class="btn btn-quiet" style="width:100%;margin-top:8px" onClick={onChooseMate}>Choose a mate</button>
      <PairStatusLine phase={pairReq.phase} />
      {a.ill && (
        <button class="btn btn-primary" style="width:100%;margin-top:8px" onClick={() => { act('animal.treat', { animalId: a.id }); }}>
          {sim.standing?.treatCostCents ? `Treat now ($${fmtMoney(sim.standing.treatCostCents)} vet fee)` : 'Treat now (vet fee)'}
        </button>
      )}
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:8px">
        {a.quarantined !== true && <button class="btn btn-quiet" style={a.quarantined === false ? 'grid-column:1 / -1' : ''} onClick={() => { act('animal.quarantine', { animalId: a.id }); }}>Quarantine</button>}
        {a.quarantined !== false && <button class="btn btn-quiet" style={a.quarantined === true ? 'grid-column:1 / -1' : ''} disabled={sim.standing?.importHolds?.includes(a.id) === true} onClick={() => { act('animal.release', { animalId: a.id }); }}>{sim.standing?.importHolds?.includes(a.id) ? 'Import hold - awaiting customs clearance' : 'Release'}</button>}
      </div>
      {!a.quarantined && sim.enclosures.some((en) => en.id !== a.enclosure) && (
        <div style="margin-top:8px">
          <div class="eyebrow">Move to another enclosure</div>
          <div style="display:grid;grid-template-columns:1fr auto;gap:6px;margin-top:6px">
            <select aria-label="Move to enclosure" value={moveTarget} onChange={(e) => { setMoveTarget(e.currentTarget.value); setMoveAttempt(null); }} style="padding:10px 12px;border:var(--ui-border);border-radius:10px;background:var(--ui-parchment);font:inherit">
              <option value="">Move to...</option>
              {sim.enclosures.filter((en) => en.id !== a.enclosure).map((en) => <option key={en.id} value={en.id}>{en.id} ({en.type}, {en.animalCount} there)</option>)}
            </select>
            <button class="btn btn-primary" disabled={!moveTarget} onClick={sendMove}>Move</button>
          </div>
          {moveAttempt && (() => {
            const outcome = moveAttempt.requestId === 'sent' ? null : (sim.moveOutcomes.find((o) => o.requestId === moveAttempt.requestId) ?? null);
            const agedOut = outcome === null && moveOutcomeAgedOut(moveAttempt.requestId, sim.moveOutcomes);
            if (moveAttempt.requestId !== 'sent' && outcome === null && !agedOut) return <div class="row-label" style="margin-top:6px">Move sent - waiting for the ranch to confirm.</div>;
            const rows = resolveMoveRows([a.id], moveAttempt, outcome);
            return (
              <div style="margin-top:6px">
                {agedOut && <div class="row-label">Result aged out of the ranch log (keeps the latest 20) - check the pen above.</div>}
                {rows.map((r) => (
                  <div key={r.animalId} class="row-label" style="white-space:normal">
                    {r.kind === 'not-moved' ? `Not moved - ${r.reason ?? ''}` : r.kind === 'moved' ? `Moved to ${moveTarget}.` : r.kind === 'sent' ? 'Move sent.' : `Failed - ${r.reason ?? ''}`}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}
      <button class="btn btn-quiet" style="width:100%;margin-top:8px" onClick={() => { openPanel({ kind: 'observe', species: a.species }); }}>
        Observe species
      </button>
      <button class="btn btn-quiet" style="width:100%;margin-top:8px" onClick={(e) => { const el = e.currentTarget; setSellOpen(!sellOpen); window.setTimeout(() => { const sc = el.closest('.hero-card-scroll'); if (sc instanceof HTMLElement) { sc.scrollTo({ top: Math.max(0, sc.scrollTop + el.getBoundingClientRect().top - sc.getBoundingClientRect().top - 4), behavior: 'smooth' }); } }, 30); }}>
        {sellOpen ? 'Close sale options' : 'Sell or auction'}
      </button>
      {sellOpen && (
        <div style="margin-top:10px;padding-top:10px;border-top:1px solid var(--line, rgba(60,50,40,.25))">
          <div class="eyebrow">Buyer type</div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:6px">
            {SALE_CHANNELS.map((c) => (
              <button key={c.id} class={channel === c.id ? 'btn btn-primary' : 'btn btn-quiet'} onClick={() => { setChannel(c.id); }}>{c.name}</button>
            ))}
          </div>
          <div class="row-label" style="white-space:normal;margin-top:4px">{SALE_CHANNELS.find((c) => c.id === channel)?.hint}</div>
          {juris.length > 0 && (
            <>
              <div class="eyebrow" style="margin-top:12px">Destination</div>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:6px">
                <button class={dest === '' ? 'btn btn-primary' : 'btn btn-quiet'} onClick={(e) => { setDest(''); scrollSellActionsIntoView(e.currentTarget); }}>Domestic</button>
                {juris.map((j) => (
                  <button key={j.id} class={dest === j.id ? 'btn btn-primary' : 'btn btn-quiet'} onClick={(e) => { setDest(j.id); scrollSellActionsIntoView(e.currentTarget); }}>{j.name}</button>
                ))}
              </div>
              {destDef && (
                <div class="row-label" style="white-space:normal;margin-top:6px">
                  {destDef.name}: {destDef.quarantineDays} day quarantine on arrival, {destDef.bannedCount} banned species. Needs an export licence.
                  {sim.standing?.exportCounts?.[a.species] ? ` Exported this year: ${sim.standing.exportCounts[a.species]} ${speciesLabel(a.species)}.` : ''}
                </div>
              )}
              {sim.standing?.importHolds?.includes(a.id) && (
                <div class="row-label" style="white-space:normal;margin-top:4px">Import hold: this animal is in import quarantine.</div>
              )}
            </>
          )}
          <label style="display:block;font-size:13px;margin-top:12px">Auction minimum hammer price ($)<input aria-label="Auction reserve dollars" type="number" min="0" step="0.01" value={auctionReserve} onInput={e=>{setAuctionReserve(e.currentTarget.value);setAuctionReview(false);}} style="display:block;width:100%"/></label>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:12px">
            <button class="btn btn-primary" onClick={() => { act('animal.sell', { animalId: a.id, channel, ...(dest ? { destination: dest } : {}) }); setSellOpen(false); }}>Sell</button>
            <button class="btn btn-quiet" disabled={!validReserve||!sim.business?.auctionTerms} onClick={() => {setAuctionReview(true);}}>Review auction</button>
          </div>
          <div class="row-label" style="white-space:normal;margin-top:6px">Auction term {sim.business?.auctionTerms?.durationDays??'unavailable'} game days; house fee {sim.business?.auctionTerms?sim.business.auctionTerms.feeBp/100:'unavailable'}% plus existing freight/permit charges after a sale. Reserve is the hammer minimum, not guaranteed net proceeds. Below reserve returns the same animal unsold without a house fee. Rejections show as alerts.</div>
          {auctionReview&&<div role="group" aria-label="Review auction listing" style="background:var(--ui-parchment);border:var(--ui-border);border-radius:var(--ui-radius);padding:12px;margin-top:10px;position:relative;isolation:isolate"><p style="font-size:13px">List {a.name} ({a.id}) with ${fmtMoney(reserveCents)} minimum hammer price? Animal leaves your herd for escrow until sale, withdrawal or unsold return. A zero reserve allows any bid under current rules.</p><button class="btn btn-primary" disabled={!validReserve} onClick={()=>{act('auction.list',{animalId:a.id,reserveCents,...(dest?{destination:dest}:{})});setAuctionReview(false);}}>List selected animal</button><button class="btn btn-quiet" onClick={()=>{setAuctionReview(false);}}>Not now</button></div>}
        </div>
      )}

      <div style="margin:10px 0 14px">
        {detailRow(IconPaw, 'Coat', coatLabel(a.species, a.coat))}
        {detailRow(IconLeaf, 'Temperament', a.temperament)}
        {detailRow(IconDna, 'Genetics', a.traits.length ? `${a.traits.length} trait${a.traits.length === 1 ? '' : 's'}` : 'No notable traits')}
      </div>
      {reference && (
        <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin:0 0 14px;padding:10px 12px">
          <div class="eyebrow">Species reference</div>
          <div class="row-value">{reference.name}</div>
          <div style="font-size:13px;line-height:1.5;margin-top:6px">
            Social type: {reference.social}. Catalog minimum group: {reference.minGroup}. Catalog male proportion: {reference.maleRatioPct}%.
          </div>
          <div style="font-size:13px;line-height:1.5">Activity: {reference.activity}.</div>
          <div style="font-size:12px;line-height:1.5;margin-top:6px;color:var(--ui-ink-soft)">
            Reference only. These catalog values do not yet change live social stress, welfare or tour visibility. Effects are awaiting balance approval.
          </div>
        </div>
      )}
      {a.knowledge && (
        <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin:0 0 14px;padding:10px 12px">
          <div class="eyebrow">Knowledge</div>
          <div class="row" style="margin:0;border:none;padding:8px 0;min-height:44px">
            <span class="row-main">
              <div class="row-label">Health</div>
              <div class="row-value" style="font-size:13px">
                {a.knowledge.healthKnown ? `${a.knowledge.health} - verified` : `est. ${a.knowledge.health} - ${Math.round(a.knowledge.healthConfidenceBp / 100)}% confident`}
              </div>
            </span>
            {!a.knowledge.healthKnown && (
              <button class="btn btn-quiet" style="padding:8px 12px;font-size:11px;min-height:44px" onClick={() => { act('animal.vet_check', { animalId: a.id }); }}>
                Vet check ${fmtMoney(a.knowledge.costs.vetCheckCents)}
              </button>
            )}
          </div>
          <div class="row" style="margin:0;border:none;padding:8px 0;min-height:44px;border-top:var(--ui-border)">
            <span class="row-main">
              <div class="row-label">Quality</div>
              <div class="row-value" style="font-size:13px">
                {a.knowledge.qualityKnown ? 'Verified by inspection' : `Estimate - ${Math.round(a.knowledge.qualityConfidenceBp / 100)}% confident`}
              </div>
              {a.knowledge.qualityComponents && (
                <div class="row-label" style="font-size:11px;text-transform:none;letter-spacing:0.01em;white-space:normal">
                  Trophy traits {a.knowledge.qualityComponents.trophy ?? 'no data'} - morph price x{(a.knowledge.qualityComponents.morphMultiplierBp / 10000).toFixed(1)} - health {a.knowledge.qualityComponents.health} - outbreeding {a.knowledge.qualityComponents.outbreeding} (F {(a.knowledge.qualityComponents.inbreedingBp / 100).toFixed(1)}%). Composite score pending a formula decision.
                </div>
              )}
            </span>
            {!a.knowledge.qualityKnown && (
              <button class="btn btn-quiet" style="padding:8px 12px;font-size:11px;min-height:44px" onClick={() => { act('animal.inspect', { animalId: a.id }); }}>
                Inspect ${fmtMoney(a.knowledge.costs.inspectCents)}
              </button>
            )}
          </div>
          <div class="row" style="margin:0;border:none;padding:8px 0;min-height:44px;border-top:var(--ui-border)">
            <span class="row-main">
              <div class="row-label">Genetics</div>
              <div class="row-value" style="font-size:13px">{a.knowledge.geneticsRecordAvailable===false?'Locus record unavailable':a.knowledge.geneticsKnown ? 'Recorded DNA on file' : 'Unknown'}</div>
            </span>
            {a.knowledge.geneticsRecordAvailable!==false&&!a.knowledge.geneticsKnown && (
              <button class="btn btn-quiet" style="padding:8px 12px;font-size:11px;min-height:44px" onClick={() => { act('animal.dna_test', { animalId: a.id }); }}>
                DNA test ${fmtMoney(a.knowledge.costs.dnaTestCents)}
              </button>
            )}
          </div>
          <AnimalLifeLog animalId={a.id} />
          <div class="row-label" style="margin-top:4px;text-transform:none;letter-spacing:0.01em;white-space:normal">
            Estimates can flatter or undersell. Reveals post to the ledger; rejections (already known, short cash) surface as alerts. Placeholder costs and confidence, flagged in sim.
          </div>
        </div>
      )}
      </div>
    </div>
    </>
  );
}

export function HerdScreen() {
  const { bridge, sim, panel, openPanel, closePanel } = useStore();
  const openId = panel?.kind === 'animal' ? panel.id : null;
  const openAnimal = sim.animals.find((a) => a.id === openId) ?? null;
  const [pairFor, setPairFor] = useState<string | null>(null);
  const pairAnimal = sim.animals.find((a) => a.id === pairFor) ?? null;
  // P125 (bounded D21): search, species/enclosure/sex/sickness/age filters, sort,
  // a reversible group-by view and localStorage saved filters.
  // (Plan's "groups" stays open for an authored-groups reading.)
  // P125 (D22): true virtualization - a measured window renders only the rows
  // near the viewport; heights come from ResizeObserver measurement (no fixed
  // row-height assumption), estimates seed from the running measured average,
  // and spacers hold the full scroll height so position/selection stay anchored.
  const [saleReviewId,setSaleReviewId]=useState<string|null>(null);
  const [saleAttemptId,setSaleAttemptId]=useState<string|null>(null);
  const [saleSending,setSaleSending]=useState(false);
  const [filters, setFilters] = useState<HerdFilterState>(DEFAULT_HERD_FILTERS);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const heightsRef = useRef(new Map<string, number>());
  const estRef = useRef({ row: 92, header: 34 }); // seeds; replaced by measured averages
  const [, setVTick] = useState(0);
  const [savedFilters, setSavedFilters] = useState<SavedHerdFilter[]>(() => loadSavedHerdFilters(localStorage));
  const [savedChoice, setSavedChoice] = useState('');
  // P125 (D22): bulk move selection. Sim (herd.move) is the authority on
  // per-item outcomes; the UI only pre-marks what the projection can see.
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [moveTarget, setMoveTarget] = useState('');
  const [treatmentAttempt, setTreatmentAttempt] = useState<{requestId:string;names:Record<string,string>;count:number;cap:number} | null>(null);
  const [treatmentSending,setTreatmentSending]=useState(false);
  // The sent attempt: requestId PLUS the pre-exclusions captured at send time.
  // Snapshotting matters - after a successful move the animal IS in the target,
  // so recomputing eligibility post-move would mislabel it "already there".
  const [moveAttempt, setMoveAttempt] = useState<{ requestId: string; preExcluded: ReadonlyMap<string, string> } | null>(null);
  const toggleSelect = (id: string) => {
    setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
    setMoveAttempt(null);setTreatmentAttempt(null);
  };
  const [saveName, setSaveName] = useState('');
  const setF = (patch: Partial<HerdFilterState>) => { setFilters((f) => ({ ...f, ...patch })); scrollRef.current?.scrollTo({ top: 0 }); };
  const speciesList = [...new Set(sim.animals.map((a) => a.species))].sort();
  const enclosureList = [...new Set(sim.animals.map((a) => a.enclosure).filter((e): e is string => e !== null))].sort();
  const filtered = applyHerdFilters(sim.animals, filters, sim.tick);
  // Flat render list: rows, or group headers interleaved with rows. Grouping
  // runs over the FULL filtered set (not a slice) so counts stay true.
  type VItem = { kind: 'header'; key: string; label: string; count: number } | { kind: 'row'; key: string; a: UiAnimal };
  const items: VItem[] = filters.groupBy === 'none'
    ? filtered.map((a) => ({ kind: 'row', key: a.id, a }))
    : groupHerdAnimals(filtered, filters.groupBy).flatMap((g) => [
        { kind: 'header' as const, key: `h:${g.key}`, label: g.label, count: g.animals.length },
        ...g.animals.map((a): VItem => ({ kind: 'row', key: a.id, a })),
      ]);
  const rowOrdinal = new Map<string, number>();
  { let n = 0; for (const it of items) { if (it.kind === 'row') { n += 1; rowOrdinal.set(it.key, n); } } }
  // Offsets: measured heights where observed, kind estimate otherwise. The
  // .vlist flex gap (12px) separates every rendered child, spacers included.
  const V_GAP = 12;
  const hOf = (it: VItem): number => heightsRef.current.get(it.key) ?? (it.kind === 'row' ? estRef.current.row : estRef.current.header);
  const offsets: number[] = new Array<number>(items.length);
  { let acc = 0; for (let i = 0; i < items.length; i++) { offsets[i] = acc; acc += hOf(items[i]) + V_GAP; } }
  const totalH = items.length === 0 ? 0 : offsets[items.length - 1] + hOf(items[items.length - 1]);
  // Window: items intersecting the viewport plus 600px overscan. Before mount
  // (refs unset) render the first 30 items; spacers keep full height anyway.
  let vStart = 0;
  let vEnd = Math.min(items.length - 1, 29);
  const scEl = scrollRef.current;
  const lEl = listRef.current;
  if (scEl !== null && lEl !== null && items.length > 0) {
    const scRect = scEl.getBoundingClientRect();
    const winTop = scRect.top - lEl.getBoundingClientRect().top - 600;
    const winBot = scRect.top - lEl.getBoundingClientRect().top + scRect.height + 600;
    while (vStart < items.length - 1 && offsets[vStart] + hOf(items[vStart]) < winTop) vStart += 1;
    vEnd = items.length - 1;
    while (vEnd > vStart && offsets[vEnd] > winBot) vEnd -= 1;
  }
  // Measure rendered children; correct the cache + estimates when sizes differ.
  useEffect(() => {
    const el = listRef.current;
    if (el === null) return;
    const ro = new ResizeObserver((entries) => {
      let changed = false;
      for (const en of entries) {
        const t = en.target as HTMLElement;
        const key = t.dataset['vkey'];
        if (key === undefined || key === '') continue;
        const h = t.getBoundingClientRect().height;
        if (h <= 0) continue;
        const prev = heightsRef.current.get(key);
        if (prev === undefined || Math.abs(prev - h) > 0.5) { heightsRef.current.set(key, h); changed = true; }
      }
      if (changed) {
        let rs = 0, rn = 0, hs = 0, hn = 0;
        for (const [k, v] of heightsRef.current) { if (k.startsWith('h:')) { hs += v; hn += 1; } else { rs += v; rn += 1; } }
        if (rn > 0) estRef.current.row = rs / rn;
        if (hn > 0) estRef.current.header = hs / hn;
        setVTick((t) => t + 1);
      }
    });
    el.querySelectorAll('[data-vkey]').forEach((n) => { ro.observe(n); });
    return () => { ro.disconnect(); };
  });
  const saveCurrent = () => {
    const name = saveName.trim();
    if (name === '') return;
    const next = upsertSavedHerdFilter(savedFilters, name, filters);
    setSavedFilters(next); persistSavedHerdFilters(localStorage, next); setSaveName(''); setSavedChoice(name);
  };
  const applySaved = (name: string) => {
    setSavedChoice(name);
    const hit = savedFilters.find((s) => s.name === name);
    if (hit) { setFilters(hit.state); scrollRef.current?.scrollTo({ top: 0 }); }
  };
  const removeSaved = () => {
    if (savedChoice === '') return;
    const next = deleteSavedHerdFilter(savedFilters, savedChoice);
    setSavedFilters(next); persistSavedHerdFilters(localStorage, next); setSavedChoice('');
  };
  return (
    <>
      <div class="screen-scroll herd-screen" ref={scrollRef} onScroll={() => { setVTick((t) => t + 1); }}>
        <div class="eyebrow eyebrow-rule">Herd - {filtered.length === sim.animals.length ? sim.animals.length : `${filtered.length} of ${sim.animals.length}`} animal{sim.animals.length === 1 ? '' : 's'}</div>
        {sim.animals.length > 3 && (
          <>
          <div style="display:grid;grid-template-columns:1fr auto auto;gap:6px;margin:6px 0">
            <input type="search" aria-label="Search herd" placeholder="Search herd" value={filters.q} onInput={(e) => { setF({ q: e.currentTarget.value }); }} style="min-width:0;padding:8px 10px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft)" />
            <select aria-label="Filter species" value={filters.species} onChange={(e) => { setF({ species: e.currentTarget.value }); }} style="padding:8px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft);min-width:0">
              <option value="all">All species</option>
              {speciesList.map((sp) => <option key={sp} value={sp}>{sp}</option>)}
            </select>
            <select aria-label="Sort herd" value={filters.sort} onChange={(e) => { setF({ sort: e.currentTarget.value as HerdFilterState['sort'] }); }} style="padding:8px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft);min-width:0">
              <option value="name">Name</option>
              <option value="species">Species</option>
              <option value="health">Needs care</option>
            </select>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;margin:0 0 6px">
            <select aria-label="Filter enclosure" value={filters.enclosure} onChange={(e) => { setF({ enclosure: e.currentTarget.value }); }} style="padding:8px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft);min-width:0">
              <option value="all">All enclosures</option>
              {enclosureList.map((en) => <option key={en} value={en}>{en}</option>)}
            </select>
            <select aria-label="Filter sex" value={filters.sex} onChange={(e) => { setF({ sex: e.currentTarget.value as HerdFilterState['sex'] }); }} style="padding:8px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft);min-width:0">
              <option value="all">Any sex</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
            </select>
            <select aria-label="Filter sickness" value={filters.sick} onChange={(e) => { setF({ sick: e.currentTarget.value as HerdFilterState['sick'] }); }} style="padding:8px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft);min-width:0">
              <option value="all">Any sickness</option>
              <option value="sick">Sick</option>
              <option value="healthy">Not sick</option>
            </select>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;margin:0 0 6px">
            <input type="number" min={0} step={1} inputMode="numeric" aria-label="Minimum age in game years" placeholder="Min game yrs" value={filters.ageMinY ?? ''} onInput={(e) => { const v = e.currentTarget.value; setF({ ageMinY: v === '' || !Number.isFinite(Number(v)) ? null : Math.max(0, Math.floor(Number(v))) }); }} style="min-width:0;padding:8px 10px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft)" />
            <input type="number" min={0} step={1} inputMode="numeric" aria-label="Maximum age in game years" placeholder="Max game yrs" value={filters.ageMaxY ?? ''} onInput={(e) => { const v = e.currentTarget.value; setF({ ageMaxY: v === '' || !Number.isFinite(Number(v)) ? null : Math.max(0, Math.floor(Number(v))) }); }} style="min-width:0;padding:8px 10px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft)" />
            <select aria-label="Group herd list" value={filters.groupBy} onChange={(e) => { setF({ groupBy: e.currentTarget.value as HerdFilterState['groupBy'] }); }} style="padding:8px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft);min-width:0">
              <option value="none">No grouping</option>
              <option value="species">By species</option>
              <option value="enclosure">By enclosure</option>
            </select>
          </div>
          <div style="display:grid;grid-template-columns:1fr auto;gap:6px;margin:0 0 6px">
            <select aria-label="Saved herd filters" value={savedChoice} onChange={(e) => { applySaved(e.currentTarget.value); }} style="padding:8px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft);min-width:0">
              <option value="">Saved filters…</option>
              {savedFilters.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select>
            <button class="btn btn-quiet" style="background:var(--ui-parchment-soft)" onClick={removeSaved} disabled={savedChoice === ''}>Delete</button>
          </div>
          <div style="display:grid;grid-template-columns:1fr auto;gap:6px;margin:0 0 6px">
            <input aria-label="Name for saved filter" placeholder="Name this filter" value={saveName} onInput={(e) => { setSaveName(e.currentTarget.value); }} style="min-width:0;padding:8px 10px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft)" />
            <button class="btn btn-primary" onClick={saveCurrent}>Save</button>
          </div>
          </>
        )}
        {sim.animals.length > 0 && (
          // P135: gate herds (2026-10-21 ruling) start unassigned - one pen is
          // already a valid move target, so the old "two pens" gate hid the only
          // way to house a fresh Standard/Hard herd.
          <div style="margin:6px 0">
            <button class={selecting ? 'btn btn-primary' : 'btn btn-quiet'} style={selecting ? '' : 'background:var(--ui-parchment-soft)'} onClick={() => { setSelecting(!selecting); setSelected(new Set()); setMoveTarget(''); setMoveAttempt(null); }}>
              {selecting ? `Done selecting (${String(selected.size)})` : 'Select animals'}
            </button>
          </div>
        )}
        {selecting && selected.size > 0 && (() => {
          const target = sim.enclosures.find((e) => e.id === moveTarget);
          const selectedAnimals = sim.animals.filter((a) => selected.has(a.id));
          // UI pre-marks only what the projection knows: quarantine, already-in
          // and a full pen. Species compatibility is the sim's call (herd.move
          // reports per-item reasons as alerts; the report below reflects them).
          const tCap = target ? Math.floor(target.areaHa * sim.enclosureTypes[target.type].density_per_ha) : 0;
          const preMark = (a: UiAnimal): string | null => {
            if (!target) return null;
            if (a.enclosure === target.id) return 'already there';
            if (a.quarantined === true) return 'quarantined';
            return null;
          };
          const eligible = selectedAnimals.filter((a) => preMark(a) === null);
          const roomNote = target && target.animalCount + eligible.length > tCap ? ' - pen may fill up' : '';
          const doMove = () => {
            if (!target || eligible.length === 0) return;
            // Snapshot pre-exclusions NOW; post-move state would rewrite them.
            const preExcluded = new Map<string, string>();
            for (const a of selectedAnimals) { const pre = preMark(a); if (pre !== null) preExcluded.set(a.id, pre); }
            // sendTracked resolves with the bus command id (unique per command,
            // incl. rapid repeats); the outcome record (v63 log, dedup-free) is
            // matched by that id, so every attempt shows ITS OWN results.
            void bridge.sendTracked('herd.move', { animalIds: eligible.map((a) => a.id), enclosureId: target.id })
              .then((id) => { setMoveAttempt({ requestId: id ?? 'sent', preExcluded }); })
              .catch(() => { setMoveAttempt(null); }); // rejection surfaces as a command_rejected alert
          };
          return (
            <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);padding:10px;margin:6px 0">
              <div class="row-value">{selected.size} selected</div>
              <div style="display:grid;grid-template-columns:1fr auto;gap:6px;margin-top:6px">
                <select aria-label="Move to enclosure" value={moveTarget} onChange={(e) => { setMoveTarget(e.currentTarget.value); setMoveAttempt(null); }} style="padding:8px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft);min-width:0">
                  <option value="">Move to...</option>
                  {sim.enclosures.map((en) => <option key={en.id} value={en.id}>{en.id} ({en.type}, {en.animalCount} there)</option>)}
                </select>
                <button class="btn btn-primary" disabled={!target || eligible.length === 0} onClick={doMove}>Move {target ? eligible.length : ''}</button>
              </div>
              {!moveAttempt && target && <div class="row-label" style="margin-top:6px">{eligible.length} of {selected.size} eligible for checking{roomNote} - the ranch confirms each move</div>}
              {!moveAttempt && target && selectedAnimals.filter((a) => preMark(a) !== null).map((a) => <div key={a.id} class="row-label" style="margin-top:2px">{a.name}: {preMark(a)}</div>)}
              {moveAttempt && target && (() => {
                const outcome = moveAttempt.requestId === 'sent' ? null : (sim.moveOutcomes.find((o) => o.requestId === moveAttempt.requestId) ?? null);
                // Eviction: the v63 log is bounded. If it is full and this
                // attempt's id is absent, its record aged out - say so honestly
                // rather than waiting forever or implying success.
                const agedOut = outcome === null && moveOutcomeAgedOut(moveAttempt.requestId, sim.moveOutcomes);
                if (moveAttempt.requestId !== 'sent' && outcome === null && !agedOut) return <div class="row-label" style="margin-top:8px">Move sent - waiting for the ranch to report...</div>;
                // Attempt evidence only (unit-tested in test/ui-move-report.test.ts):
                // send-time snapshot for pre-exclusions, the attempt's own
                // outcome record for everything submitted. Post-move state
                // (e.g. the animal now being in the target) never rewrites it.
                const rows = resolveMoveRows(selectedAnimals.map((a) => a.id), moveAttempt, outcome);
                const nameOf = (id: string): string => selectedAnimals.find((a) => a.id === id)?.name ?? id;
                return (
                  <div style="margin-top:8px">
                    {agedOut && <div class="row-label">Result aged out of the ranch log (keeps the latest 20) - check each animal's pen above.</div>}
                    {rows.map((r) => (
                      <div key={r.animalId} class="row-label">
                        {nameOf(r.animalId)}: {r.kind === 'not-moved' ? `not moved - ${r.reason ?? ''}` : r.kind === 'moved' ? 'moved' : r.kind === 'sent' ? 'sent' : `failed${r.reason !== null ? ` - ${r.reason}` : ''}`}
                      </div>
                    ))}
                  </div>
                );
              })()}
              {(() => {
                const ill=selectedAnimals.filter(a=>a.ill);
                const fee=sim.standing?.treatCostCents;
                const cap=fee===undefined?null:fee*ill.length;
                const receipt=treatmentAttempt?(sim.treatmentReceipts??[]).find(r=>r.requestId===treatmentAttempt.requestId):undefined;
                return <div style="border-top:var(--ui-border);margin-top:12px;padding-top:10px">
                  <div class="row-value">{treatmentAttempt?`Submitted ${treatmentAttempt.count} animals - reviewed cap $${fmtMoney(treatmentAttempt.cap)}`:'Treat selected ill animals'}</div>
                  <div class="row-label" style="white-space:normal">{!treatmentAttempt&&<>{ill.length} ill of {selectedAnimals.length} selected. {fee===undefined?'Current vet fee unavailable.':`$${fmtMoney(fee)} each; maximum $${fmtMoney(cap??0)}. Healthy or removed animals are not charged. Short funds can leave some untreated.`}</>}</div>
                  <button class="btn btn-primary" style="margin-top:8px" disabled={ill.length===0||cap===null||treatmentSending||treatmentAttempt!==null} onClick={()=>{
                    if(cap===null||ill.length===0)return;
                    const names=Object.fromEntries(ill.map(a=>[a.id,a.name]));const count=ill.length;setTreatmentSending(true);setTreatmentAttempt(null);
                    void bridge.sendTracked('herd.treat',{animalIds:ill.map(a=>a.id),maxTotalCents:cap}).then(id=>{setTreatmentAttempt({requestId:id??'sent',names,count,cap});}).catch(()=>{setTreatmentAttempt(null);}).finally(()=>{setTreatmentSending(false);});
                  }}>{treatmentAttempt?'Attempt submitted':treatmentSending?'Sending...':`Treat ${ill.length} - up to $${fmtMoney(cap??0)}`} </button>
                  {treatmentAttempt&&!receipt&&<div class="row-label" style="white-space:normal;margin-top:8px">No treatment receipt available for this attempt yet. Check alerts if the price changed or the command was rejected.</div>}
                  {receipt&&<div style="margin-top:8px">
                    <div class="row-value">Treatment result: {receipt.treated.length} treated - ${fmtMoney(receipt.chargedCents)} charged</div>
                    {receipt.treated.map(id=><div key={id} class="row-label">{treatmentAttempt?.names[id]??id}: treated</div>)}
                    {receipt.failed.map(f=><div key={f.animalId} class="row-label" style="white-space:normal">{treatmentAttempt?.names[f.animalId]??f.animalId}: not treated - {treatmentReason(f.reason)}</div>)}
                  </div>}
                </div>;
              })()}
              <div style="border-top:var(--ui-border);margin-top:12px;padding-top:10px">
                <div class="row-value">Sell exact selected stock</div>
                <p class="row-label" style="white-space:normal">Permanent sale through the existing stock channel, not a hunt or meat action. Each sale changes species saturation, so selection order affects later prices. No substitute animal is chosen. Existing pregnancies or breeding plans can lose a parent when you sell.</p>
                <button class="btn btn-quiet" disabled={saleSending||selectedAnimals.length===0} onClick={()=>{setSaleSending(true);setSaleReviewId(null);setSaleAttemptId(null);void bridge.sendTracked('herd.sale.review',{animalIds:selectedAnimals.map(a=>a.id)}).then(id=>{setSaleReviewId(id??null);}).catch(()=>{setSaleReviewId(null);}).finally(()=>{setSaleSending(false);});}}>Review {selectedAnimals.length} stock sales</button>
                {saleReviewId&&(()=>{const review=sim.herdSales?.reviews.find(r=>r.requestId===saleReviewId);const receipt=sim.herdSales?.receipts.find(r=>r.requestId===saleAttemptId);if(!review)return <p class="row-label" style="white-space:normal">Review not available yet or aged out. Review again before selling.</p>;return <div role="group" aria-label="Exact bulk stock sale review">
                  <p class="row-label" style="white-space:normal">Only the exact IDs below will be submitted in this order. Eligible rows set a minimum net price. Lower current prices refuse that item; higher prices are accepted. Failed items stay, later eligible items continue. Recorded review is not a sale.</p>
                  {review.rows.map(r=><p key={r.animalId} class="row-label" style="white-space:normal">{r.name} ({r.animalId}): {r.netCents===null?`not for sale - ${r.reason??'unavailable'}`:`minimum net $${stockMoney(r.netCents)}${r.netCents<0?' (deducted from ranch cash)':''}`}</p>)}
                  {!saleAttemptId&&<><button class="btn btn-primary" disabled={saleSending||!review.rows.some(r=>r.netCents!==null)} onClick={()=>{setSaleSending(true);void bridge.sendTracked('herd.sale.confirm',{reviewId:review.requestId}).then(id=>{setSaleAttemptId(id??null);}).catch(()=>{setSaleAttemptId(null);}).finally(()=>{setSaleSending(false);});}}>Permanently sell these exact animals</button><button class="btn btn-quiet" onClick={()=>{setSaleReviewId(null);}}>Keep animals</button></>}
                  {saleAttemptId&&!receipt&&<p class="row-label" style="white-space:normal">Sale submitted. No receipt available for this attempt yet; check rejection alerts.</p>}
                  {receipt&&<><div class="row-value">{receipt.sold.length} sold - net ${stockMoney(receipt.sold.reduce((n,r)=>n+r.netCents,0))}</div>{receipt.sold.map(r=><p key={r.animalId} class="row-label" style="white-space:normal">{r.name} ({r.animalId}): sold, net ${stockMoney(r.netCents)}</p>)}{receipt.failed.map(r=><p key={r.animalId} class="row-label" style="white-space:normal">{r.name} ({r.animalId}): retained or unavailable - {r.reason}</p>)}</>}
                </div>;})()}
              </div>
              <button class="btn btn-quiet" style="margin-top:8px" onClick={() => { setSelected(new Set()); setMoveTarget(''); setMoveAttempt(null); setTreatmentAttempt(null); }}>Clear selection</button>
            </div>
          );
        })()}
        {(sim.herdSales?.receipts.length??0)>0&&<details open style="border:var(--ui-border);padding:10px;margin:8px 0;background:var(--ui-parchment-soft)"><summary>Recent exact stock sale receipts</summary>{sim.herdSales?.receipts.slice().reverse().map(r=><div key={r.requestId} style="border-top:var(--ui-border);padding-top:8px;margin-top:8px"><div class="row-value">{r.sold.length} sold - net ${stockMoney(r.sold.reduce((n,x)=>n+x.netCents,0))}</div>{r.sold.map(x=><p key={x.animalId} class="row-label" style="white-space:normal">{x.name} ({x.animalId}): sold, net ${stockMoney(x.netCents)}</p>)}{r.failed.map(x=><p key={x.animalId} class="row-label" style="white-space:normal">{x.name} ({x.animalId}): not sold - {x.reason}</p>)}</div>)}<p class="row-label" style="white-space:normal">Latest 20 attempts; retry keys survive receipt rotation and reload. No meat or cull action.</p></details>}
        {(sim.treatmentReceipts??[]).length>0&&<details style="border:var(--ui-border);padding:10px;margin:8px 0;background:var(--ui-parchment-soft)">
          <summary>Recent treatment receipts</summary>
          {[...(sim.treatmentReceipts??[])].reverse().map(r=><div key={r.requestId} style="margin-top:8px;border-top:var(--ui-border);padding-top:6px">
            <div class="row-value">Day {Math.floor(r.tick/24)+1}: {r.treated.length} treated - ${fmtMoney(r.chargedCents)}</div>
            <div class="row-label" style="white-space:normal">Reviewed maximum ${fmtMoney(r.maxTotalCents)}. {r.treated.map(id=>sim.animals.find(a=>a.id===id)?.name??`Former animal ${id}`).join(', ')||'No animals treated'}</div>
            {r.failed.map(f=><div key={f.animalId} class="row-label" style="white-space:normal">{sim.animals.find(a=>a.id===f.animalId)?.name??`Former animal ${f.animalId}`}: not treated - {treatmentReason(f.reason)}</div>)}
          </div>)}
          <div class="row-label" style="margin-top:6px;white-space:normal">Latest 20 attempts. Retrying the same request never charges again, even after its receipt leaves this list.</div>
        </details>}
        {filtered.length === 0 && <p style="font-size:13px;color:var(--ui-ink-soft)">No animals match.</p>}
        <div class="vlist" ref={listRef} role="list" aria-label="Herd animals">
          {vStart > 0 && <div aria-hidden style={`height:${String(offsets[vStart] - V_GAP)}px;flex:0 0 auto`} />}
          {items.slice(vStart, vEnd + 1).map((it) => it.kind === 'header'
            ? <div key={it.key} data-vkey={it.key} class="eyebrow eyebrow-rule" style="margin:0;align-self:center">{it.label} ({it.count})</div>
            : (
              <div key={it.key} data-vkey={it.key} role="listitem" aria-setsize={filtered.length} aria-posinset={rowOrdinal.get(it.key) ?? 0}>
                <AnimalRow a={it.a} onOpen={() => { openPanel({ kind: 'animal', id: it.a.id }); }} selecting={selecting} selected={selected.has(it.a.id)} onToggle={() => { toggleSelect(it.a.id); }} />
              </div>
            ))}
          {vEnd < items.length - 1 && <div aria-hidden style={`height:${String(totalH - offsets[vEnd] - hOf(items[vEnd]) - V_GAP)}px;flex:0 0 auto`} />}
        </div>
      </div>
      {panel?.kind === 'observe' && <ObserveSheet {...(panel.species !== undefined ? { initialSpecies: panel.species } : {})} />}
      {openAnimal && (
        <Sheet hero label={`${openAnimal.name} details`} onClose={closePanel}>
          <AnimalDetail a={openAnimal} onChooseMate={() => { setPairFor(openAnimal.id); }} />
        </Sheet>
      )}
      {pairAnimal && <PairingSheet a={pairAnimal} onClose={() => { setPairFor(null); }} onPaired={() => { /* the card's breed feedback watches sim state */ }} />}
    </>
  );
}
